import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import { FileError } from '../../components/FileError';
import { validateFile } from '../../lib/fileValidation';
import { api } from '../../lib/api';

// Small helper that wraps a button + hidden file input + inline error.
// All asset uploads on this page funnel through here so validation is
// applied uniformly — even though the actual upload endpoint isn't wired
// yet (TODO: POST /api/v1/brand/assets), the rejection path already works.
function UploadAssetButton({ label, accept, onAccepted, className, children, ariaLabel }) {
  const inputRef = useRef(null);
  const [error, setError] = useState(null);

  function handleSelect(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setError(null);
    const result = validateFile(file, 'asset');
    if (!result.valid) {
      setError({ key: result.errorKey, params: result.params });
      return;
    }
    onAccepted?.(file);
  }

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => inputRef.current?.click()}
        aria-label={ariaLabel}
      >
        {children ?? label}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleSelect}
        style={{ display: 'none' }}
      />
      {error && <FileError errorKey={error.key} params={error.params} />}
    </>
  );
}

const ASSET_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/gif';

const COLOURS = [
  { name: 'Cream',   hex: '#F8F7F2' },
  { name: 'Peach',   hex: '#F4E9D6' },
  { name: 'Blush',   hex: '#F3D8CD' },
  { name: 'Caramel', hex: '#C08767' },
  { name: 'Terra',   hex: '#8B3B29', roleKey: 'primary' },
  { name: 'Orange',  hex: '#EA9951' },
  { name: 'Gold',    hex: '#FDB707' },
  { name: 'Sage',    hex: '#9FB19D', roleKey: 'success' },
  { name: 'Olive',   hex: '#8F9653' },
  { name: 'Sky',     hex: '#BCDDEC' },
  { name: 'Navy',    hex: '#002F50', roleKey: 'headings' },
  { name: 'Dark',    hex: '#232526', roleKey: 'bodyText' },
];

const FONTS = [
  { roleKey: 'headings', name: 'The Seasons',         cls: 'font-row--seasons' },
  { roleKey: 'body',     name: 'Now',                 cls: 'font-row--now' },
  { roleKey: 'accent',   name: 'Bw Gradual Regular' },
  { roleKey: 'display',  name: 'Jockey One Regular' },
];

// Mock orgs used when the worker is unreachable in dev (RULE #16 — every API
// call needs a fallback). Same shape as api.organisations.list().organisations.
const MOCK_ORGS = [
  { id: 'mock-1', name: 'UNESCO',        logoB64: null },
  { id: 'mock-2', name: 'Europa Nostra', logoB64: null },
  { id: 'mock-3', name: 'MKiDN',         logoB64: null },
];

function makeId() {
  return Math.random().toString(36).slice(2, 11);
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('FileReader failed'));
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}

function ColourRow({ colour }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  function copy() {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(colour.hex).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  }

  return (
    <div className="colour-row">
      <span className="colour-row__swatch" style={{ background: colour.hex }} aria-hidden="true" />
      <span className="colour-row__name">{colour.name}</span>
      {colour.roleKey && (
        <span className="colour-row__role">{t(`brand.assets.colours.${colour.roleKey}`)}</span>
      )}
      <span className="colour-row__hex">{colour.hex}</span>
      <button
        type="button"
        className={'colour-row__copy' + (copied ? ' colour-row__copy--copied' : '')}
        onClick={copy}
        aria-label={`Copy ${colour.hex}`}
        title={`Copy ${colour.hex}`}
      >
        {copied ? '✓' : '📋'}
      </button>
    </div>
  );
}

export function AssetsTab() {
  const { t } = useTranslation();
  const [orgs, setOrgs] = useState([]);
  const [orgsUsingMock, setOrgsUsingMock] = useState(false);
  const [graphicsError, setGraphicsError] = useState(null);
  // Local-only graphics library. TODO: wire to brand_assets API when implemented
  // (POST /api/v1/brand/assets, GET /api/v1/brand/assets?type=graphic).
  const [graphics, setGraphics] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.organisations
      .list()
      .then((data) => {
        if (cancelled) return;
        setOrgs(data?.organisations ?? []);
        setOrgsUsingMock(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[brand-assets] orgs fetch failed, using mock:', err.message);
        setOrgs(MOCK_ORGS);
        setOrgsUsingMock(true);
      });
    return () => { cancelled = true; };
  }, []);

  async function handleGraphicAccepted(file) {
    setGraphicsError(null);
    try {
      const dataUrl = await readAsDataUrl(file);
      setGraphics((prev) => [
        ...prev,
        { id: makeId(), name: file.name.replace(/\.[^.]+$/, ''), dataUrl, originalFilename: file.name },
      ]);
    } catch {
      setGraphicsError({ key: 'upload.errors.fileCorrupt', params: {} });
    }
  }

  function renameGraphic(id, name) {
    setGraphics((prev) => prev.map((g) => (g.id === id ? { ...g, name } : g)));
  }

  function removeGraphic(id) {
    setGraphics((prev) => prev.filter((g) => g.id !== id));
  }

  return (
    <div className="tab-page">
      <p className="tab-page__intro">{t('brand.assets.intro')}</p>

      {/* LOGOS */}
      <section className="assets-section">
        <h2 className="assets-section__title">{t('brand.assets.sections.logos')}</h2>
        <p className="assets-section__hint">{t('brand.assets.logos.hint')}</p>
        <div className="logo-grid">
          <div className="logo-card logo-card--dark">
            <div className="logo-card__placeholder">F8M</div>
            <div className="logo-card__name">{t('brand.assets.logos.primary')}</div>
            <div className="logo-card__meta">PNG · 2400×800</div>
            <div className="logo-card__actions">
              <UploadAssetButton
                className="button button--ghost"
                accept={ASSET_ACCEPT}
                onAccepted={(file) => console.log('[brand-assets] primary logo accepted:', file.name)}
              >
                {t('brand.assets.logos.uploadNew')}
              </UploadAssetButton>
              <button type="button" className="button button--ghost">{t('brand.assets.logos.download')}</button>
              <button type="button" className="button button--ghost">{t('brand.assets.logos.copyUrl')}</button>
            </div>
          </div>
          <div className="logo-card">
            <div className="logo-card__placeholder">F8M</div>
            <div className="logo-card__name">{t('brand.assets.logos.secondary')}</div>
            <div className="logo-card__meta">PNG · 1200×400</div>
            <div className="logo-card__actions">
              <UploadAssetButton
                className="button button--ghost"
                accept={ASSET_ACCEPT}
                onAccepted={(file) => console.log('[brand-assets] secondary logo accepted:', file.name)}
              >
                {t('brand.assets.logos.uploadNew')}
              </UploadAssetButton>
              <button type="button" className="button button--ghost">{t('brand.assets.logos.download')}</button>
              <button type="button" className="button button--ghost">{t('brand.assets.logos.copyUrl')}</button>
            </div>
          </div>
        </div>
      </section>

      {/* COLOURS */}
      <section className="assets-section">
        <h2 className="assets-section__title">{t('brand.assets.sections.brandColours')}</h2>
        <p className="assets-section__hint">{t('brand.assets.colours.copyHint')}</p>
        <div className="colour-grid">
          {COLOURS.map((colour) => (
            <ColourRow key={colour.hex} colour={colour} />
          ))}
        </div>
      </section>

      {/* FONTS */}
      <section className="assets-section">
        <h2 className="assets-section__title">{t('brand.assets.sections.fonts')}</h2>
        <div className="font-list">
          {FONTS.map((font) => (
            <div key={font.name} className={'font-row' + (font.cls ? ` ${font.cls}` : '')}>
              <span className="font-row__role">{t(`brand.assets.fonts.${font.roleKey}`)}</span>
              <span className="font-row__name">{font.name}</span>
              <span className="font-row__preview">{t('brand.assets.fonts.preview')}</span>
            </div>
          ))}
        </div>
        <button type="button" className="button button--ghost" style={{ marginTop: 'var(--space-3)' }}>
          + {t('brand.assets.fonts.uploadFont')}
        </button>
      </section>

      {/* PARTNER LOGOS — pulled from organisations. Logos are managed on the
          org record (People → Organisations), not here. */}
      <section className="assets-section">
        <h2 className="assets-section__title">{t('brand.assets.sections.partnerLogos')}</h2>
        <p className="assets-section__hint">{t('brand.assets.partnerLogos.hint')}</p>
        {orgsUsingMock && (
          <div className="dev-notice">{t('brand.assets.partnerLogos.loadError')}</div>
        )}
        {orgs.length === 0 && !orgsUsingMock ? (
          <p className="list-section__empty">{t('brand.assets.partnerLogos.empty')}</p>
        ) : (
          <div className="asset-strip">
            {orgs.map((org) => (
              <div key={org.id} className="asset-thumb">
                {org.logoB64 ? (
                  <img className="asset-thumb__logo" src={org.logoB64} alt={org.name} />
                ) : (
                  <span className="asset-thumb__icon" aria-hidden="true">🏢</span>
                )}
                <span>{org.name}</span>
              </div>
            ))}
          </div>
        )}
        <Link to="/people" className="assets-section__manage-link">
          {t('brand.assets.partnerLogos.manageLink')} →
        </Link>
      </section>

      {/* GRAPHICS — local-only library. TODO: wire to brand_assets API when implemented. */}
      <section className="assets-section">
        <h2 className="assets-section__title">{t('brand.assets.sections.graphics')}</h2>
        <p className="assets-section__hint">{t('brand.assets.graphics.hint')}</p>
        <div className="asset-strip">
          {graphics.map((g) => (
            <div key={g.id} className="asset-thumb asset-thumb--graphic">
              <button
                type="button"
                className="asset-thumb__remove"
                onClick={() => removeGraphic(g.id)}
                aria-label={t('common.remove')}
              >
                ×
              </button>
              <img className="asset-thumb__logo" src={g.dataUrl} alt={g.name} />
              <input
                className="asset-thumb__name-input"
                type="text"
                value={g.name}
                onChange={(e) => renameGraphic(g.id, e.target.value)}
                aria-label={t('brand.assets.graphics.nameLabel')}
              />
            </div>
          ))}
          <UploadAssetButton
            className="asset-thumb asset-thumb--upload"
            accept={ASSET_ACCEPT}
            onAccepted={handleGraphicAccepted}
          >
            <span className="asset-thumb__icon" aria-hidden="true">+</span>
            <span>{t('brand.assets.graphics.upload')}</span>
          </UploadAssetButton>
        </div>
        {graphicsError && (
          <FileError errorKey={graphicsError.key} params={graphicsError.params} />
        )}
      </section>
    </div>
  );
}
